import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DashboardPageLayout } from '@/components/layouts/dashboard-page-layout'
import { TabContainer } from '@/components/organisms/accounts-receivable-manager/tab-container'
import { css } from '@emotion/core'
import { exportAccountsReceivableReceiptForManager, fetchAccountsReceivableReceiptForManager } from '@/apis/aipass'
import { AccountsReceivableReceipt } from '@/models/accounts-receivable-manager'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { LoaderContextCreator } from '@/contexts/loader'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import dayjs from 'dayjs'
import uuid from 'react-uuid'
import { Select } from '@/components/atoms/select'
import { Select as AntSelect } from 'antd'
import { useDayjsRange } from '@/hooks/use-dayjs-range'
import { TypeLabel } from '@/components/organisms/accounts-receivable-manager/type-label'

export const AccountsReceivableManagerReceiptHistory: React.FC = () => {
  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { errorHandler } = useErrorHandler()
  const { dayjsRange } = useDayjsRange()

  const [searchResponse, setSearchResponse] = useState<{
    indicator: { receiptPrice: number }
    results: AccountsReceivableReceipt[]
  }>()
  const [receiptDate, setReceiptDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'))

  const viewMonthRange = dayjsRange({
    start: dayjs().startOf('month').subtract(5, 'month'),
    end: dayjs().endOf('month'),
    interval: 1,
    unit: 'month',
  }).reverse()

  const doSearch = async (searchCondition: { receiptDate: string }) => {
    try {
      setIsLoading(true)
      setSearchResponse(await fetchAccountsReceivableReceiptForManager(searchCondition))
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const doExport = async () => {
    try {
      setIsLoading(true)
      const { url } = await exportAccountsReceivableReceiptForManager({ receiptDate })
      window.open(url, '_blank')
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    doSearch({ receiptDate })
  }, [receiptDate])

  return (
    <DashboardPageLayout title={t('A/R Management')}>
      <TabContainer currentTab="Deposit history">
        <div className="headline-block">
          <div
            css={css({
              display: 'flex',
              alignItems: 'center',
              fontSize: 14,
              color: 'rgb(39, 39, 39)',
              gap: 8,
            })}
          >
            <div>{t('Deposit day')}</div>
            <Select customStyle={css({ width: 150, paddingBottom: 0 })} value={receiptDate} onChange={v => setReceiptDate(v.toString())}>
              {viewMonthRange.map(d => (
                <AntSelect.Option key={uuid()} value={d.format('YYYY-MM-DD')}>
                  {d.format(t('YYYY-MM'))}
                </AntSelect.Option>
              ))}
            </Select>
          </div>
          <div>
            <Button
              buttonType={ButtonType.NonStyle}
              width={170}
              height={32}
              fontSize={12}
              icon={require('@/static/images/csv.svg')}
              onClick={doExport}
            >
              {t('Export CSV')}
            </Button>
          </div>
        </div>

        <div className="indicator-block">
          <div>
            <div className="sub-title">{t('Receipt total')}</div>
            <div className="title">¥{searchResponse?.indicator.receiptPrice.toLocaleString() || '-'}</div>
          </div>
        </div>

        <div className="table-block">
          {searchResponse?.results.length !== 0 ? (
            <div css={tableStyle}>
              <div className="table-header">
                <div className="table-row">
                  <div className="table-cell col-reconciliation-date">{t('Reconciliation at')}</div>
                  <div className="table-cell col-receipt-date">{t('Deposit day')}</div>
                  <div className="table-cell col-ar">{t('Accounts receivable')}</div>
                  <div className="table-cell col-type">{t('Type')}</div>
                  <div className="table-cell col-price">{t('Receipt price')}</div>
                  <div className="table-cell col-summary">{t('Summary')}</div>
                </div>
              </div>
              <div className="table-body">
                {searchResponse?.results.map(responseRow => (
                  <div className="table-row" key={responseRow.id}>
                    <div className="table-cell col-reconciliation-date">
                      {dayjs(responseRow.reconciliationAt).format('YYYY/M/D HH:mm:ss')}
                    </div>
                    <div className="table-cell col-receipt-date">{dayjs(responseRow.receiptAt).format('YYYY/M/D')}</div>
                    <div className="table-cell col-ar">{responseRow.customer.name}</div>
                    <div className="table-cell col-type">
                      <TypeLabel isDiscount={responseRow.isDiscount} />
                    </div>
                    <div className="table-cell col-price">¥{responseRow.receiptPrice.toLocaleString()}</div>
                    <div className="table-cell col-summary">{responseRow.accountDescription}</div>
                  </div>
                ))}
                <div className="table-row">
                  <div className="table-cell col-reconciliation-date">{t('Total')}</div>
                  <div className="table-cell col-receipt-date"></div>
                  <div className="table-cell col-ar"></div>
                  <div className="table-cell col-type"></div>
                  <div className="table-cell col-price">¥{searchResponse?.indicator.receiptPrice.toLocaleString()}</div>
                  <div className="table-cell col-summary">{}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="empty-table">
              <h1>{t('No data available')}</h1>
            </div>
          )}
        </div>
      </TabContainer>
    </DashboardPageLayout>
  )
}

const tableStyle = css({
  '.col-reconciliation-date': { minWidth: 150, maxWidth: 150 },
  '.col-receipt-date': { minWidth: 100, maxWidth: 100 },
  '.col-ar': {
    minWidth: 'calc((100% - 8px - 150px - 100px - 60px - 100px - 150px - (16px * 6)))',
    maxWidth: 'calc((100% - 8px - 150px - 100px - 60px - 100px - 150px - (16px * 6)))',
  },
  '.col-type': { minWidth: 60, maxWidth: 60 },
  '.col-price': { minWidth: 100, maxWidth: 100, textAlign: 'right' },
  '.col-summary': { minWidth: 150, maxWidth: 150 },
})
